import React, { useState, useEffect } from 'react';
import { Plus, AlertCircle, Info, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import DataTable from '../../components/admin/DataTable';
import ImageUpload from '../../components/admin/ImageUpload';

const AnnouncementsManagement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title_ar: '',
    title_en: '',
    content_ar: '',
    content_en: '',
    image: '',
    link: '',
    link_text_ar: 'اقرأ المزيد',
    link_text_en: 'Read More',
    type: 'info',
    priority: 0,
    show_on_home: true,
    show_on_news: true,
    is_active: true,
    start_date: '',
    end_date: '',
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/announcements/all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await response.json();
      setAnnouncements(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing 
        ? `${BACKEND_URL}/api/announcements/${editing.id}` 
        : `${BACKEND_URL}/api/announcements/`;
      
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        fetchAnnouncements();
        resetForm();
        alert('تم الحفظ بنجاح');
      }
    } catch (error) {
      alert('حدث خطأ');
    }
  };

  const resetForm = () => {
    setFormData({
      title_ar: '',
      title_en: '',
      content_ar: '',
      content_en: '',
      image: '',
      link: '',
      link_text_ar: 'اقرأ المزيد',
      link_text_en: 'Read More',
      type: 'info',
      priority: 0,
      show_on_home: true,
      show_on_news: true,
      is_active: true,
      start_date: '',
      end_date: '',
    });
    setEditing(null);
    setShowForm(false);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'info': return <Info className="w-4 h-4 text-blue-500" />;
      case 'success': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'warning': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'danger': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const columns = [
    { 
      header: 'النوع', 
      render: (row) => (
        <div className="flex items-center gap-2">
          {getTypeIcon(row.type)}
          <span className="text-sm">{row.type}</span>
        </div>
      )
    },
    { header: 'العنوان', accessor: 'title_ar' },
    { 
      header: 'الحالة', 
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {row.is_active ? 'نشط' : 'غير نشط'}
        </span>
      )
    },
    { 
      header: 'الأولوية', 
      render: (row) => <span className="font-semibold">{row.priority}</span>
    },
    {
      header: 'مواقع العرض',
      render: (row) => (
        <div className="text-xs">
          {row.show_on_home && <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded ml-1">الرئيسية</span>}
          {row.show_on_news && <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">الأخبار</span>}
        </div>
      )
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-[#0F388E]">إدارة الإعلانات</h1>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#0F388E]">
          <Plus className="ml-2 w-5 h-5" />
          إضافة إعلان
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editing ? 'تعديل' : 'إضافة'} إعلان</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">العنوان بالعربية *</label>
                  <input
                    type="text"
                    value={formData.title_ar}
                    onChange={(e) => setFormData({...formData, title_ar: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Title in English</label>
                  <input
                    type="text"
                    value={formData.title_en}
                    onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">المحتوى بالعربية *</label>
                  <textarea
                    value={formData.content_ar}
                    onChange={(e) => setFormData({...formData, content_ar: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    rows="4"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Content in English</label>
                  <textarea
                    value={formData.content_en}
                    onChange={(e) => setFormData({...formData, content_en: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    rows="4"
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <ImageUpload
                  label="صورة الإعلان (اختياري)"
                  value={formData.image}
                  onChange={(value) => setFormData({...formData, image: value})}
                  category="news"
                />
              </div>

              {/* Link */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">الرابط (اختياري)</label>
                  <input
                    type="url"
                    value={formData.link}
                    onChange={(e) => setFormData({...formData, link: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">نص الرابط بالعربية</label>
                  <input
                    type="text"
                    value={formData.link_text_ar}
                    onChange={(e) => setFormData({...formData, link_text_ar: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Link Text in English</label>
                  <input
                    type="text"
                    value={formData.link_text_en}
                    onChange={(e) => setFormData({...formData, link_text_en: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
              </div>

              {/* Settings */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">النوع</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="info">معلومات</option>
                    <option value="success">نجاح</option>
                    <option value="warning">تحذير</option>
                    <option value="danger">خطر</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">الأولوية</label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">تاريخ البداية</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">تاريخ النهاية</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
              </div>

              {/* Display Options */}
              <div className="space-y-3">
                <label className="block text-sm font-medium">خيارات العرض</label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.show_on_home}
                      onChange={(e) => setFormData({...formData, show_on_home: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span>عرض في الصفحة الرئيسية</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.show_on_news}
                      onChange={(e) => setFormData({...formData, show_on_news: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span>عرض في صفحة الأخبار</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span>نشط</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>إلغاء</Button>
                <Button type="submit" className="bg-[#0F388E]">حفظ</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <DataTable
            columns={columns}
            data={announcements}
            onEdit={(item) => { 
              setEditing(item); 
              setFormData({
                ...item,
                start_date: item.start_date ? item.start_date.split('T')[0] : '',
                end_date: item.end_date ? item.end_date.split('T')[0] : '',
              }); 
              setShowForm(true); 
            }}
            onDelete={async (item) => {
              if (window.confirm('هل أنت متأكد من الحذف؟')) {
                await fetch(`${BACKEND_URL}/api/announcements/${item.id}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                });
                fetchAnnouncements();
              }
            }}
            loading={loading}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AnnouncementsManagement;
